import { jsx as _jsx, jsxs as _jsxs } from "@emotion/react/jsx-runtime";
import { Container } from '@pixi/react';
import * as PIXI from 'pixi.js';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createToggleRevealHandOutbound } from '../../../api/outbound/pokerOutbounds';
import { useSocketSend } from '../../../context/webSocket/webSocketContext';
import { useAppSelector } from '../../../redux/store';
import { isDefaultShowdown } from '../../../types/pokerTypes';
import { ALL_POKER_VARIANT_PROPERTIES } from '../../../types/pokerVariantTypes';
import { HOLE_CARDS_OUTLINE_FILTER } from '../../../utils/pixiFilterUtils';
import { isTestEnvironment } from '../../../utils/utils';
import { HoleCardsOverlayContainer } from './holeCardsOverlayContainer';
const CONTAINER_PIXI_DATA = isTestEnvironment() ? { type: 'hole-cards' } : null;
const BUTTON_CONTAINER_PIXI_DATA = isTestEnvironment() ? { type: 'hole-cards-button' } : null;
function getOverlayMode(isRevealAvailable, isHovering, isRevealingThisHand, isSelfFolded) {
    if (!isRevealAvailable) {
        return undefined;
    }
    else if (isHovering) {
        if (isRevealingThisHand) {
            return 'revealing';
        }
        else {
            return 'reveal';
        }
    }
    else {
        if (isRevealingThisHand) {
            return 'revealing';
        }
        else if (isSelfFolded) {
            return 'folded';
        }
        else {
            return undefined;
        }
    }
}
export const HoleCardsWrapperContainer = ({ x, y, groupContainerW, groupContainerH, children }) => {
    const socketSend = useSocketSend();
    const outlineFilterContainerRef = useRef(null);
    const [isHovering, setIsHovering] = useState(false);
    const hasHoleCards = useAppSelector((state) => !!state.poker.holeCards?.length);
    const isSelfFolded = useAppSelector((state) => state.poker.isSelfFolded);
    const activeHandId = useAppSelector((state) => state.poker.activeHandId);
    const isRevealingThisHand = useAppSelector((state) => !!state.poker.isRevealingThisHand);
    const isRevealAvailable = useAppSelector((state) => {
        if (!state.poker.pokerVariant) {
            return false;
        }
        if (!ALL_POKER_VARIANT_PROPERTIES[state.poker.pokerVariant].allowsReveal) {
            return false;
        }
        if (state.poker.isSelfFolded) {
            // if you have folded (and it's asserted elsewhere that you have hole cards), reveal is available
            return true;
        }
        if (state.poker.inboundShowdowns.length === 1 &&
            state.poker.currentShowdown &&
            isDefaultShowdown(state.poker.currentShowdown)) {
            // if you haven't folded and there is a single-pot default showdown (and it's asserted elsewhere that you have hole cards), reveal is available
            return true;
        }
        return false;
    });
    const onMouseOver = useCallback(() => {
        if (!isRevealAvailable) {
            return;
        }
        setIsHovering(true);
    }, [isRevealAvailable]);
    const onMouseOut = useCallback(() => {
        if (!isRevealAvailable) {
            return;
        }
        setIsHovering(false);
    }, [isRevealAvailable]);
    useEffect(() => {
        if (!isRevealAvailable) {
            setIsHovering(false);
        }
    }, [isRevealAvailable]);
    useEffect(() => {
        if (outlineFilterContainerRef.current) {
            outlineFilterContainerRef.current.filters = isHovering ? [HOLE_CARDS_OUTLINE_FILTER] : [];
        }
    }, [isHovering]);
    const onClick = useCallback(() => {
        if (activeHandId === undefined) {
            // should not happen
            return;
        }
        socketSend(createToggleRevealHandOutbound(!isRevealingThisHand, activeHandId));
    }, [isRevealingThisHand, socketSend, activeHandId]);
    const hitArea = useMemo(() => new PIXI.Rectangle(-groupContainerW / 2, -groupContainerH / 2, groupContainerW, groupContainerH), [groupContainerW, groupContainerH]);
    if (!hasHoleCards) {
        return null;
    }
    return (_jsxs(Container, { "pixi-data": CONTAINER_PIXI_DATA, x: x, y: y, children: [_jsx(Container, { "pixi-data": BUTTON_CONTAINER_PIXI_DATA, ref: outlineFilterContainerRef, cursor: isRevealAvailable ? 'pointer' : 'default', interactive: isRevealAvailable, pointerdown: onClick, mouseover: onMouseOver, mouseout: onMouseOut, hitArea: hitArea, children: children }), _jsx(HoleCardsOverlayContainer, { mode: getOverlayMode(isRevealAvailable, isHovering, isRevealingThisHand, isSelfFolded) })] }));
};
